import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Skeleton } from '@lifeomic/chroma-react/components/Skeleton';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { getTestProps } from '../../util/testProps';
import { heroSpace, heroSurface } from './memberExtractionTheme';

const messages = defineMessages({
  loadingExtractions: {
    id: 'phc-web-toolkit.memberExtractions.loading',
    defaultMessage: 'Loading extractions',
  },
  loadingPages: {
    id: 'phc-web-toolkit.memberExtractions.pdfPages.loading',
    defaultMessage: 'Loading pages',
  },
  loadingSummary: {
    id: 'phc-web-toolkit.memberExtractions.summary.loading',
    defaultMessage: 'Loading summary',
  },
  loadingHistory: {
    id: 'phc-web-toolkit.memberExtractions.history.loading',
    defaultMessage: 'Loading earlier results',
  },
});

export const pdfLetterRatio = 11 / 8.5;

export const pdfThumbnailWidth = 140;

const useStyles = makeStyles(() => ({
  bone: {
    display: 'block',
    flex: '0 0 auto',
    maxWidth: '100%',
    // Translucent so the placeholders stay faint on white and on muted cards.
    backgroundColor: 'rgba(0, 0, 0, 0.035)',
    borderRadius: heroSurface.radiusSmall,
    '&::after': {
      background:
        'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent)',
    },
    '@media (prefers-reduced-motion: reduce)': {
      '&::after': {
        animation: 'none',
      },
    },
  },
}));

const Bone: React.FC<{
  width: number | string;
  height: number | string;
}> = ({ width, height }) => {
  const classes = useStyles();
  return (
    <Skeleton
      animation="shine"
      width={width}
      height={height}
      className={classes.bone}
    />
  );
};

const LoadingRegion: React.FC<{
  label: string;
  style?: React.CSSProperties;
}> = ({ label, style, children }) => (
  <div
    role="status"
    aria-label={label}
    style={style}
    {...getTestProps('member-extraction-loading')}
  >
    {children}
  </div>
);

const columnStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: heroSpace.regular,
};

const cardStyle = {
  ...columnStyle,
  padding: heroSpace.regular,
  backgroundColor: heroSurface.background,
  borderRadius: heroSurface.radiusLarge,
  boxShadow: heroSurface.shadowSmall,
};

const lines = (widths: string[], height = 12) =>
  widths.map((width, index) => (
    <Bone key={index} width={width} height={height} />
  ));

const fillStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: heroSpace.regular,
  boxSizing: 'border-box' as const,
  height: '100%',
  minHeight: 0,
  padding: heroSpace.regular,
  overflow: 'hidden',
};

// Enough rows to fill a tall page; the container clips whatever doesn't fit.
const listRowCount = 12;

export const MemberExtractionDocumentListSkeleton: React.FC = () => {
  const { formatMessage } = useIntl();
  return (
    <LoadingRegion
      label={formatMessage(messages.loadingExtractions)}
      style={fillStyle}
    >
      {Array.from({ length: listRowCount }, (_, index) => (
        <Bone key={index} width="100%" height={64} />
      ))}
    </LoadingRegion>
  );
};

type PdfPageSkeletonProps = {
  width: number;
  height?: number;
};

export const MemberExtractionPdfPageSkeleton: React.FC<
  PdfPageSkeletonProps
> = ({ width, height = Math.round(width * pdfLetterRatio) }) => (
  <div aria-hidden>
    <Bone width={width} height={height} />
  </div>
);

const pagePlaceholderCount = 3;

const pageRowStyle = {
  display: 'flex',
  gap: heroSpace.regular,
  overflow: 'hidden',
};

const pagePlaceholders = () =>
  Array.from({ length: pagePlaceholderCount }, (_, index) => (
    <Bone
      key={index}
      width={pdfThumbnailWidth}
      height={Math.round(pdfThumbnailWidth * pdfLetterRatio)}
    />
  ));

export const MemberExtractionPdfPagesSkeleton: React.FC = () => {
  const { formatMessage } = useIntl();
  return (
    <LoadingRegion
      label={formatMessage(messages.loadingPages)}
      style={pageRowStyle}
    >
      {pagePlaceholders()}
    </LoadingRegion>
  );
};

export const MemberExtractionSummaryLinesSkeleton: React.FC = () => {
  const { formatMessage } = useIntl();
  return (
    <LoadingRegion
      label={formatMessage(messages.loadingSummary)}
      style={{ ...columnStyle, gap: 8 }}
    >
      {lines(['100%', '90%', '65%'])}
    </LoadingRegion>
  );
};

export const MemberExtractionSummarySkeleton: React.FC = () => (
  <div style={cardStyle}>
    <MemberExtractionSummaryLinesSkeleton />
  </div>
);

export const MemberExtractionHistorySkeleton: React.FC = () => {
  const { formatMessage } = useIntl();
  return (
    <LoadingRegion
      label={formatMessage(messages.loadingHistory)}
      style={{ ...columnStyle, gap: heroSpace.tight }}
    >
      {lines(['120px', '96px'])}
    </LoadingRegion>
  );
};

export const MemberExtractionDocumentDetailSkeleton: React.FC = () => {
  const { formatMessage } = useIntl();
  return (
    <LoadingRegion
      label={formatMessage(messages.loadingExtractions)}
      style={fillStyle}
    >
      <Bone width="100%" height={36} />
      <div
        style={{
          display: 'flex',
          flex: '1 1 auto',
          minHeight: 0,
          gap: heroSpace.regular,
        }}
      >
        <div style={{ flex: '1 1 0', minWidth: 0 }}>
          <Bone width="100%" height="100%" />
        </div>
        <div style={{ flex: '1 1 0', minWidth: 0 }}>
          <Bone width="100%" height="100%" />
        </div>
      </div>
    </LoadingRegion>
  );
};
